import {
  formaterJour,
  formaterMontant,
  niveauPermisSurEntite,
  referentiel as r,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { api, type Bail, corps, lire, requeteMoi, requetes, type Tiers } from '../api'
import { requeteAccesTemporaires } from '../composants/AccesTemporaires'
import { DepenseIaDeLEspace } from '../composants/EspacesPrives'
import { type Champ, Formulaire } from '../composants/Formulaire'
import { formaterInstant, Historique } from '../composants/Historique'
import { Icone } from '../composants/Icone'
import { LogoConnu } from '../composants/Logo'
import { Ligne, Section, Vide } from '../composants/Section'
import {
  LIBELLES_FREQUENCE,
  LIBELLES_NIVEAU_ROLE,
  LIBELLES_REGIME_IMPOT,
  LIBELLES_REGIME_TVA,
  LIBELLES_SOURCE_COMPTE,
  LIBELLES_TYPE_CONTRAT,
  LIBELLES_TYPE_ENTITE,
  options,
  LIBELLES_ESPACE_DRIVE,
  LIBELLES_MODE_TRANSMISSION,
} from '../libelles'
import { champsEntite } from './Entites'

const choixTiers = (tiers: Tiers[], role?: Tiers['role']) =>
  tiers.filter((t) => !role || t.role === role).map((t) => ({ valeur: t.id, libelle: t.nom }))

const champsBien: Champ[] = [
  { nom: 'nom', libelle: 'Nom', type: 'texte', requis: true },
  { nom: 'adresse', libelle: 'Adresse', type: 'texte', requis: true },
  { nom: 'commune', libelle: 'Commune', type: 'texte', requis: true },
]

const champsBail = (tiers: Tiers[]): Champ[] => [
  {
    nom: 'locataireId',
    libelle: 'Locataire',
    type: 'choix',
    options: choixTiers(tiers, 'locataire'),
  },
  {
    nom: 'gestionnaireId',
    libelle: 'Gestionnaire',
    type: 'choix',
    options: choixTiers(tiers, 'gestionnaire'),
  },
  { nom: 'loyerCts', libelle: 'Loyer mensuel (€)', type: 'montant' },
  { nom: 'chargesCts', libelle: 'Charges mensuelles (€)', type: 'montant' },
  { nom: 'indice', libelle: 'Indice de révision', type: 'texte', aide: 'IRL, ILC, ILAT…' },
  { nom: 'dateRevision', libelle: 'Date de révision', type: 'date' },
]

const champsContrat = (tiers: Tiers[], biens: { id: string; nom: string }[]): Champ[] => [
  { nom: 'libelle', libelle: 'Libellé', type: 'texte', requis: true },
  { nom: 'tiersId', libelle: 'Tiers', type: 'choix', requis: true, options: choixTiers(tiers) },
  {
    nom: 'type',
    libelle: 'Type',
    type: 'choix',
    requis: true,
    options: options(LIBELLES_TYPE_CONTRAT),
  },
  {
    nom: 'frequence',
    libelle: 'Fréquence',
    type: 'choix',
    requis: true,
    options: options(LIBELLES_FREQUENCE),
  },
  { nom: 'montantCts', libelle: 'Montant (€)', type: 'montant' },
  {
    nom: 'bienId',
    libelle: 'Bien concerné',
    type: 'choix',
    options: biens.map((b) => ({ valeur: b.id, libelle: b.nom })),
  },
  {
    nom: 'canalFacture',
    libelle: 'Canal de facturation',
    type: 'texte',
    aide: 'Mail, plateforme, portail, courrier…',
  },
]

const champsCompte: Champ[] = [
  { nom: 'libelle', libelle: 'Libellé', type: 'texte', requis: true },
  { nom: 'banque', libelle: 'Banque', type: 'texte', requis: true },
  {
    nom: 'source',
    libelle: 'Source des relevés',
    type: 'choix',
    requis: true,
    options: options(LIBELLES_SOURCE_COMPTE),
  },
]

function montant(cts: number | null) {
  return cts === null ? null : formaterMontant(cts)
}

function resumeBail(b: Bail, tiers: Tiers[]) {
  const nom = (id: string | null) => tiers.find((t) => t.id === id)?.nom
  return [
    nom(b.locataireId) ?? 'Sans locataire',
    b.loyerCts !== null && `loyer ${montant(b.loyerCts)}`,
    b.chargesCts !== null && `charges ${montant(b.chargesCts)}`,
    b.indice && `indice ${b.indice}`,
    b.dateRevision && `révision le ${formaterJour(b.dateRevision)}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

export function EntiteFiche({ id }: { id: string }) {
  const moi = useSuspenseQuery(requeteMoi).data
  const client = useQueryClient()
  const entite = useQuery(requetes.entite(id))
  const roles = useQuery(requetes.roles(id))
  const personnes = useQuery(requetes.personnes)
  const tiers = useQuery(requetes.tiers)
  const biens = useQuery(requetes.biens(id))
  const baux = useQuery(requetes.baux(id))
  const contrats = useQuery(requetes.contrats(id))
  const comptes = useQuery(requetes.comptes(id))
  const plan = useQuery(requetes.plan(id))
  // Dépôt direct chez le portail du cabinet (Tiime) : sa connexion de justificatifs, pour ouvrir
  // le bilan depuis la fiche. Ni l'envoi par mail ni « rien à envoyer » n'en ont.
  const transmission = entite.data?.transmission
  const depotDirect =
    transmission !== undefined && transmission !== 'aucune' && transmission !== 'mail'
  const portails = useQuery({ ...requetes.portails, enabled: depotDirect })
  // Droits d'après le périmètre de la requête de la fiche, pas d'après /moi (relu moins
  // souvent : une entité créée entre-temps par l'autre administrateur n'y est pas encore).
  // Lue par un accès temporaire (lecture seule) : son échéance vient des demandes de la personne.
  const parAcces = entite.data?.droits.parAcces === true
  const acces = useQuery({ ...requeteAccesTemporaires, enabled: parAcces })
  const rafraichir = () => client.invalidateQueries()

  if (entite.isPending)
    return (
      <main>
        <Vide>Chargement…</Vide>
      </main>
    )
  if (entite.isError || !entite.data) {
    return (
      <main>
        <p className="alerte">Entité introuvable.</p>
        <Link to="/entites">Retour aux entités</Link>
      </main>
    )
  }
  const e = entite.data
  const { gestion, ecriture } = e.droits
  const finAcces = acces.data?.envoyees.find(
    (a) => a.entiteId === id && a.statut === 'accorde',
  )?.fin
  // Le niveau administrateur (accès à tout) ne s'accorde que par un administrateur, et jamais
  // sur un espace privé (niveau associé seulement, même pour un administrateur titulaire).
  const niveaux = options(LIBELLES_NIVEAU_ROLE).filter(
    (o) =>
      (moi.administrateur || o.valeur === 'associe') && niveauPermisSurEntite(o.valeur, e.privee),
  )
  const roleModifiable = (niveau: string) => gestion && (moi.administrateur || niveau === 'associe')
  const listeTiers = tiers.data ?? []
  const nomTiers = (tid: string | null) => listeTiers.find((t) => t.id === tid)?.nom
  const connexionsDepot = (portails.data?.connexions ?? []).filter(
    (c) => c.entiteId === id && c.justificatifs?.mode === e.transmission,
  )

  return (
    <main>
      <header className="entete-fiche">
        <p className="fil">
          <Link to="/entites">Entités</Link>
        </p>
        <h1>
          {e.nom} {e.archivee && <span className="badge">Archivée, lecture seule</span>}
          {!e.archivee && parAcces && (
            <span className="badge ton-ambre">
              Lecture seule, accès temporaire
              {finAcces ? ` jusqu’au ${formaterInstant(String(finAcces))}` : ''}
            </span>
          )}
        </h1>
      </header>

      {'departIncomplet' in e && e.departIncomplet.length > 0 && (
        <p className="alerte">
          Départ incomplet : {e.departIncomplet.join(' ')} Compléter la fiche (Modifier) : la règle
          se crée alors.
        </p>
      )}
      <Section titre="Informations">
        <Ligne
          edition={
            gestion && !e.archivee
              ? (fermer) => (
                  <Formulaire
                    champs={champsEntite(listeTiers, {
                      privee: e.privee,
                      compteServiceDrive: moi.compteServiceDrive,
                    })}
                    initial={e}
                    onAnnuler={fermer}
                    onValider={async (v) => {
                      await lire(
                        api.entites[':id'].$patch({
                          param: { id },
                          json: corps(r.entiteModification, v),
                        }),
                      )
                      await rafraichir()
                      fermer()
                    }}
                  />
                )
              : undefined
          }
        >
          <dl className="details">
            <dt>Type</dt>
            <dd>{[e.forme, LIBELLES_TYPE_ENTITE[e.type]].filter(Boolean).join(' · ')}</dd>
            <dt>SIREN</dt>
            <dd>{e.siren ?? 'Non renseigné'}</dd>
            <dt>TVA</dt>
            <dd>{e.regimeTva ? LIBELLES_REGIME_TVA[e.regimeTva] : 'Non renseigné'}</dd>
            <dt>Imposition</dt>
            <dd>{e.regimeImpot ? LIBELLES_REGIME_IMPOT[e.regimeImpot] : 'Non renseigné'}</dd>
            <dt>Clôture</dt>
            <dd>{e.cloture ?? 'Non renseignée'}</dd>
            <dt>Cabinet</dt>
            <dd>{nomTiers(e.cabinetId) ?? 'Aucun'}</dd>
            <dt>Plateforme agréée</dt>
            <dd>{e.plateformeAgreee ?? 'Non renseignée'}</dd>
            <dt>Justificatifs</dt>
            <dd>
              {LIBELLES_MODE_TRANSMISSION[e.transmission]}
              {e.transmission === 'mail' &&
                ` : ${e.adresseCollecte ?? 'adresse de collecte à renseigner'}`}
              {depotDirect && (
                <div className="discret">
                  Sans adresse de collecte : rien ne part par mail
                  {e.adresseCollecte ? ' (l’adresse renseignée ne sert pas)' : ''}. L’agent du Mac
                  rapproche les opérations du cabinet des factures d’achat.{' '}
                  {connexionsDepot.length > 0
                    ? connexionsDepot.map((c) => (
                        <Link key={c.id} to="/justificatifs/$id" params={{ id: c.id }}>
                          Bilan des justificatifs ({c.libelle})
                        </Link>
                      ))
                    : portails.data &&
                      'Aucune connexion du portail : la créer dans Réglages, Portails.'}
                </div>
              )}
            </dd>
            <dt>Drive</dt>
            <dd>
              {e.drivePartageId ? (
                <>Drive partagé propre, id « {e.drivePartageId} ».</>
              ) : e.privee ? (
                <span className="ton-ambre">
                  Espace privé sans Drive partagé réglé : le rangement attend.
                </span>
              ) : (
                LIBELLES_ESPACE_DRIVE[e.espaceDrive]
              )}
              {e.racineClassement ? (
                <div className="discret">
                  Rangé sous « {e.racineClassement} » selon le plan de classement.
                </div>
              ) : (
                (
                  [
                    ['Achats', e.cheminAchats],
                    ['Ventes', e.cheminVentes],
                    ['Relevés', e.cheminReleves],
                    ['Autres pièces', e.cheminAutres],
                  ] as const
                ).map(([rubrique, chemin]) => (
                  <div key={rubrique} className="discret">
                    {rubrique} :{' '}
                    {chemin ??
                      (rubrique === 'Autres pièces' ? 'à renseigner' : 'comme les autres pièces')}
                  </div>
                ))
              )}
              {!e.racineClassement && (
                <div className="discret">
                  Plan de classement : il servira dès qu’une racine de classement sera renseignée.
                </div>
              )}
            </dd>
            {e.privee && <DepenseIaDeLEspace entiteId={id} />}
            {e.identifiants.length > 0 && (
              <>
                <dt>Autres identifiants</dt>
                <dd>{e.identifiants.join(' · ')}</dd>
              </>
            )}
          </dl>
        </Ligne>
        <div className="boutons">
          <Link to="/entites/$id/plan" params={{ id }} className="bouton">
            <Icone nom="folder" taille={18} />
            Plan de classement
            {plan.data && plan.data.propositions > 0 && (
              <span className="badge ton-ambre">
                {plan.data.propositions} proposition{plan.data.propositions > 1 ? 's' : ''} à
                valider
              </span>
            )}
          </Link>
          {gestion && (
            <button
              type="button"
              className="bouton-discret"
              onClick={async () => {
                await lire(
                  api.entites[':id'].$patch({ param: { id }, json: { archivee: !e.archivee } }),
                )
                await rafraichir()
              }}
            >
              {e.archivee ? 'Désarchiver' : 'Archiver'}
            </button>
          )}
        </div>
      </Section>

      <Section
        titre="Rôles et responsables"
        ajout={
          gestion
            ? (fermer) => (
                <Formulaire
                  champs={[
                    {
                      nom: 'personneId',
                      libelle: 'Personne',
                      type: 'choix',
                      requis: true,
                      options: (personnes.data ?? [])
                        .filter((p) => p.type === 'humain')
                        .map((p) => ({ valeur: p.id, libelle: p.nom })),
                    },
                    {
                      nom: 'niveau',
                      libelle: 'Niveau',
                      type: 'choix',
                      requis: true,
                      options: niveaux,
                    },
                    { nom: 'responsable', libelle: 'Responsable', type: 'booleen' },
                  ]}
                  initial={{ niveau: 'associe' }}
                  onAnnuler={fermer}
                  onValider={async (v) => {
                    await lire(
                      api.roles.$post({ json: corps(r.roleCreation, { ...v, entiteId: id }) }),
                    )
                    await rafraichir()
                    fermer()
                  }}
                />
              )
            : undefined
        }
      >
        {roles.data?.length === 0 && <Vide>Aucun rôle.</Vide>}
        {roles.data?.map((role) => (
          <Ligne
            key={role.id}
            edition={
              roleModifiable(role.niveau)
                ? (fermer) => (
                    <Formulaire
                      champs={[
                        {
                          nom: 'niveau',
                          libelle: 'Niveau',
                          type: 'choix',
                          requis: true,
                          options: niveaux,
                        },
                        { nom: 'responsable', libelle: 'Responsable', type: 'booleen' },
                      ]}
                      initial={role}
                      onAnnuler={fermer}
                      onValider={async (v) => {
                        await lire(
                          api.roles[':id'].$patch({
                            param: { id: role.id },
                            json: corps(r.roleModification, v),
                          }),
                        )
                        await rafraichir()
                        fermer()
                      }}
                    />
                  )
                : undefined
            }
            onSupprimer={
              roleModifiable(role.niveau)
                ? async () => {
                    await lire(api.roles[':id'].$delete({ param: { id: role.id } }))
                    await rafraichir()
                  }
                : undefined
            }
          >
            <strong>{role.personne}</strong> · {LIBELLES_NIVEAU_ROLE[role.niveau]}
            {role.responsable && <span className="badge">Responsable</span>}
          </Ligne>
        ))}
      </Section>

      <Section
        titre="Biens et baux"
        ajout={
          ecriture
            ? (fermer) => (
                <Formulaire
                  champs={champsBien}
                  onAnnuler={fermer}
                  onValider={async (v) => {
                    await lire(
                      api.biens.$post({ json: corps(r.bienCreation, { ...v, entiteId: id }) }),
                    )
                    await rafraichir()
                    fermer()
                  }}
                />
              )
            : undefined
        }
        libelleAjout="Ajouter un bien"
      >
        {biens.data?.length === 0 && <Vide>Aucun bien.</Vide>}
        {biens.data?.map((bien) => (
          <div key={bien.id} className="bloc">
            <Ligne
              edition={
                ecriture
                  ? (fermer) => (
                      <Formulaire
                        champs={champsBien}
                        initial={bien}
                        onAnnuler={fermer}
                        onValider={async (v) => {
                          await lire(
                            api.biens[':id'].$patch({
                              param: { id: bien.id },
                              json: corps(r.bienModification, v),
                            }),
                          )
                          await rafraichir()
                          fermer()
                        }}
                      />
                    )
                  : undefined
              }
              onSupprimer={
                ecriture
                  ? async () => {
                      await lire(api.biens[':id'].$delete({ param: { id: bien.id } }))
                      await rafraichir()
                    }
                  : undefined
              }
            >
              <strong>{bien.nom}</strong>
              <span className="discret">
                {' '}
                {bien.adresse}, {bien.commune}
              </span>
            </Ligne>
            <div className="sous-liste">
              {baux.data
                ?.filter((b) => b.bienId === bien.id)
                .map((bail) => (
                  <Ligne
                    key={bail.id}
                    edition={
                      ecriture
                        ? (fermer) => (
                            <Formulaire
                              champs={champsBail(listeTiers)}
                              initial={bail}
                              onAnnuler={fermer}
                              onValider={async (v) => {
                                await lire(
                                  api.baux[':id'].$patch({
                                    param: { id: bail.id },
                                    json: corps(r.bailModification, v),
                                  }),
                                )
                                await rafraichir()
                                fermer()
                              }}
                            />
                          )
                        : undefined
                    }
                    onSupprimer={
                      ecriture
                        ? async () => {
                            await lire(api.baux[':id'].$delete({ param: { id: bail.id } }))
                            await rafraichir()
                          }
                        : undefined
                    }
                  >
                    Bail : {resumeBail(bail, listeTiers)}
                  </Ligne>
                ))}
              {ecriture && (
                <Section
                  titre=""
                  libelleAjout="Ajouter un bail"
                  ajout={(fermer) => (
                    <Formulaire
                      champs={champsBail(listeTiers)}
                      onAnnuler={fermer}
                      onValider={async (v) => {
                        await lire(
                          api.baux.$post({
                            json: corps(r.bailCreation, { ...v, bienId: bien.id }),
                          }),
                        )
                        await rafraichir()
                        fermer()
                      }}
                    />
                  )}
                >
                  {null}
                </Section>
              )}
            </div>
          </div>
        ))}
      </Section>

      <Section
        titre="Contrats"
        ajout={
          ecriture
            ? (fermer) => (
                <Formulaire
                  champs={champsContrat(listeTiers, biens.data ?? [])}
                  onAnnuler={fermer}
                  onValider={async (v) => {
                    await lire(
                      api.contrats.$post({
                        json: corps(r.contratCreation, { ...v, entiteId: id }),
                      }),
                    )
                    await rafraichir()
                    fermer()
                  }}
                />
              )
            : undefined
        }
      >
        {contrats.data?.length === 0 && <Vide>Aucun contrat.</Vide>}
        {contrats.data?.map((c) => (
          <Ligne
            key={c.id}
            edition={
              ecriture
                ? (fermer) => (
                    <Formulaire
                      champs={champsContrat(listeTiers, biens.data ?? [])}
                      initial={c}
                      onAnnuler={fermer}
                      onValider={async (v) => {
                        await lire(
                          api.contrats[':id'].$patch({
                            param: { id: c.id },
                            json: corps(r.contratModification, v),
                          }),
                        )
                        await rafraichir()
                        fermer()
                      }}
                    />
                  )
                : undefined
            }
            onSupprimer={
              ecriture
                ? async () => {
                    await lire(api.contrats[':id'].$delete({ param: { id: c.id } }))
                    await rafraichir()
                  }
                : undefined
            }
          >
            <span className="titre-logo">
              <LogoConnu nom={nomTiers(c.tiersId)} />
              <strong>{c.libelle}</strong>
            </span>
            <span className="discret">
              {' '}
              {[
                nomTiers(c.tiersId),
                LIBELLES_TYPE_CONTRAT[c.type],
                LIBELLES_FREQUENCE[c.frequence],
                montant(c.montantCts),
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </Ligne>
        ))}
      </Section>

      <Section
        titre="Comptes bancaires"
        ajout={
          ecriture
            ? (fermer) => (
                <Formulaire
                  champs={champsCompte}
                  initial={{ source: 'csv' }}
                  onAnnuler={fermer}
                  onValider={async (v) => {
                    await lire(
                      api.comptes.$post({ json: corps(r.compteCreation, { ...v, entiteId: id }) }),
                    )
                    await rafraichir()
                    fermer()
                  }}
                />
              )
            : undefined
        }
      >
        {comptes.data?.length === 0 && <Vide>Aucun compte.</Vide>}
        {comptes.data?.map((c) => (
          <Ligne
            key={c.id}
            edition={
              ecriture
                ? (fermer) => (
                    <Formulaire
                      champs={champsCompte}
                      initial={c}
                      onAnnuler={fermer}
                      onValider={async (v) => {
                        await lire(
                          api.comptes[':id'].$patch({
                            param: { id: c.id },
                            json: corps(r.compteModification, v),
                          }),
                        )
                        await rafraichir()
                        fermer()
                      }}
                    />
                  )
                : undefined
            }
            onSupprimer={
              ecriture
                ? async () => {
                    await lire(api.comptes[':id'].$delete({ param: { id: c.id } }))
                    await rafraichir()
                  }
                : undefined
            }
          >
            <span className="titre-logo">
              <LogoConnu nom={c.banque} />
              <strong>{c.libelle}</strong>
            </span>
            <span className="discret">
              {' '}
              {c.banque} · {LIBELLES_SOURCE_COMPTE[c.source]} · relevé couvert jusqu’au{' '}
              {c.releveCouvertJusquAu ? formaterJour(c.releveCouvertJusquAu) : 'aucun import'}
            </span>
          </Ligne>
        ))}
      </Section>

      <Section titre="Historique">
        <Historique entiteId={id} />
      </Section>
    </main>
  )
}
